import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useProjectionOverrides } from '@/hooks/useProjectionOverrides';
import { Layout } from '@/components/layout/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { useNetWorthAnalytics, convertProjectionToChartItems } from '@/api/hooks/useNetWorthAnalytics';
import {
    NetWorthSummaryCards,
    NetWorthChart,
    NetWorthEmptyState,
} from '@/components/dashboard';
import { usePeriodPreference } from '@/components/dashboard/PeriodSelector';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getErrorMessage } from '@/utils/errorHandler';
import type { ChartPeriod, AnalyticsGranularity } from '@/api/types';

/** Default placeholder summary for loading state */
const placeholderSummary = {
    currentValue: 0,
    ytdChange: null,
};

export function DashboardPage() {
    usePageTitle('Net worth');

    // Independent state for period and granularity
    // Period controls the data range (3m, 6m, 1y, Max, Future) - persisted to localStorage
    // Granularity controls display density (M/Y)
    const [period, setPeriod] = usePeriodPreference('period-net-worth', 'max');
    const [granularity, setGranularity] = useState<AnalyticsGranularity>('year');

    // Projection settings (null/undefined = server defaults), persisted to
    // localStorage so tuned values survive navigation
    const {
        fiEnabled: projectionFiEnabled,
        returnRate: projectionReturnRate,
        contribution: projectionContribution,
        debtChange: projectionDebtChange,
        years: projectionYears,
        annualExpenses: projectionAnnualExpenses,
        fiIncludedCollectionIds: projectionFiCollectionIds,
        fiWithdrawalRatePct: projectionFiWithdrawalRatePct,
        fiInvestableReturnRate: projectionFiInvestableReturnRate,
        fiSimulateWithdrawal: projectionSimulateWithdrawal,
        fiInflationRate: projectionInflationRate,
        setFiEnabled: setProjectionFiEnabled,
        setReturnRate: setProjectionReturnRate,
        setContribution: setProjectionContribution,
        setDebtChange: setProjectionDebtChange,
        setYears: setProjectionYears,
        setAnnualExpenses: setProjectionAnnualExpenses,
        setFiIncludedCollectionIds: setProjectionFiCollectionIds,
        setFiWithdrawalRatePct: setProjectionFiWithdrawalRatePct,
        setFiInvestableReturnRate: setProjectionFiInvestableReturnRate,
        setFiSimulateWithdrawal: setProjectionSimulateWithdrawal,
        setFiInflationRate: setProjectionInflationRate,
        reset: handleResetProjectionSettings,
        resetFiSettings: handleResetFiSettings,
        resetWithdrawalSimulation: handleResetWithdrawalSimulation,
    } = useProjectionOverrides();

    const { data, isLoading, isFetching, isError, error, refetch, periodAvailability, projectionData, hasDebts, autoGranularity } = useNetWorthAnalytics({
        granularity,
        period,
        projectionReturnRate,
        projectionContribution,
        projectionDebtChange,
        projectionYears,
        projectionFiEnabled,
        projectionAnnualExpenses,
        projectionFiCollectionIds,
        projectionFiMultiplier: 100 / projectionFiWithdrawalRatePct,
        projectionInvestableReturnRate: projectionFiInvestableReturnRate,
        projectionSimulateWithdrawal,
        projectionInflationRate,
    });

    // Enforce monthly granularity for short periods (on initial load and period changes)
    // This handles cases where localStorage has period=1y but granularity defaults to yearly
    useEffect(() => {
        if ((period === '3m' || period === '6m' || period === '1y') && granularity === 'year') {
            setGranularity('month');
        }
    }, [period, granularity]);

    // Auto-switch to monthly when data span is too short for yearly (fires once per mount)
    const hasAutoAdjusted = useRef(false);
    useEffect(() => {
        if (!hasAutoAdjusted.current && autoGranularity === 'month' && granularity === 'year' && period !== 'future') {
            setGranularity('month');
            hasAutoAdjusted.current = true;
        }
    }, [autoGranularity, granularity, period]);

    // Determine if user has any ledgers
    const hasAnyLedgers = data
        ? (data.totalAssetLedgers > 0 || data.totalDebtLedgers > 0)
        : false;

    // Handle period change with smart granularity switching
    // When selecting periods up to 1 year with yearly granularity,
    // automatically switch to monthly for meaningful data display
    const handlePeriodChange = useCallback((newPeriod: ChartPeriod) => {
        setPeriod(newPeriod);

        // Auto-switch to monthly granularity for periods up to 1 year
        if ((newPeriod === '3m' || newPeriod === '6m' || newPeriod === '1y') && granularity === 'year') {
            setGranularity('month');
        }

        // Auto-switch to yearly granularity for future projections
        if (newPeriod === 'future' && granularity === 'month') {
            setGranularity('year');
        }
    }, [granularity]);

    // Auto-switch to Max when the selected period becomes unavailable
    // (e.g. after deleting data) instead of leaving a disabled selection
    useEffect(() => {
        if (!data) return;
        const periodUnavailable =
            (period === '3m' && !periodAvailability.can3m) ||
            (period === '6m' && !periodAvailability.can6m) ||
            (period === '1y' && !periodAvailability.can1y) ||
            (period === 'future' && !periodAvailability.canFuture);
        if (periodUnavailable) {
            setPeriod('max');
        }
    }, [data, period, periodAvailability, setPeriod]);

    // Build chart items with isProjected flag for Future mode, reusing the
    // shared projection→chart mapping (single source of truth).
    const chartItems = useMemo(() => {
        if (period === 'future' && projectionData?.items) {
            return convertProjectionToChartItems(projectionData.items);
        }
        // For other periods, items don't have isProjected flag
        return data?.items || [];
    }, [period, projectionData?.items, data?.items]);

    return (
        <Layout>
            <div className="space-y-6">
                <PageHeader title="Net worth" />

                {/* Error State */}
                {isError && (
                    <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-4">
                        <ErrorAlert message={getErrorMessage(error)} />
                        <div className="mt-4 text-center">
                            <button
                                onClick={() => refetch()}
                                className="px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* Summary Cards - Show when user has any data */}
                {!isError && (isLoading || (data && hasAnyLedgers)) && (
                    <NetWorthSummaryCards
                        netWorthSummary={data?.netWorthSummary || placeholderSummary}
                        assetsSummary={data?.assetsSummary || placeholderSummary}
                        debtsSummary={data?.debtsSummary || placeholderSummary}
                        currency={data?.currency || 'USD'}
                        isLoading={isLoading && !data}
                    />
                )}

                {/* Empty State - No data at all */}
                {!isLoading && !isError && data && !hasAnyLedgers && (
                    <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary">
                        <NetWorthEmptyState
                            hasAnyLedgers={false}
                            isLoading={false}
                            dataPointCount={0}
                        />
                    </div>
                )}

                {/* Net Worth Chart - Show when user has data (empty state shown inline for insufficient data points) */}
                {!isError && (isLoading || (data && hasAnyLedgers)) && (
                    <NetWorthChart
                        items={data ? chartItems : []}
                        currency={data?.currency || ''}
                        period={period}
                        onPeriodChange={handlePeriodChange}
                        periodAvailability={periodAvailability}
                        granularity={granularity}
                        onGranularityChange={setGranularity}
                        hasDebts={hasDebts}
                        isLoading={isLoading && !data}
                        isFetching={isFetching}
                        projectionData={projectionData}
                        projectionFiEnabled={projectionFiEnabled}
                        projectionReturnRate={projectionReturnRate}
                        projectionContribution={projectionContribution}
                        projectionDebtChange={projectionDebtChange}
                        projectionYears={projectionYears ?? projectionData?.projectionYears}
                        projectionAnnualExpenses={projectionAnnualExpenses}
                        projectionFiWithdrawalRatePct={projectionFiWithdrawalRatePct}
                        projectionFiCollectionIds={projectionFiCollectionIds}
                        projectionFiInvestableReturnRate={projectionFiInvestableReturnRate}
                        projectionSimulateWithdrawal={projectionSimulateWithdrawal}
                        projectionInflationRate={projectionInflationRate}
                        onProjectionFiEnabledChange={setProjectionFiEnabled}
                        onProjectionReturnRateChange={setProjectionReturnRate}
                        onProjectionContributionChange={setProjectionContribution}
                        onProjectionDebtChangeChange={setProjectionDebtChange}
                        onProjectionYearsChange={setProjectionYears}
                        onProjectionAnnualExpensesChange={setProjectionAnnualExpenses}
                        onProjectionFiWithdrawalRatePctChange={setProjectionFiWithdrawalRatePct}
                        onProjectionFiCollectionIdsChange={setProjectionFiCollectionIds}
                        onProjectionFiInvestableReturnRateChange={setProjectionFiInvestableReturnRate}
                        onProjectionFiInvestableReturnRateReset={() => setProjectionFiInvestableReturnRate(null)}
                        onProjectionSimulateWithdrawalChange={setProjectionSimulateWithdrawal}
                        onProjectionInflationRateChange={setProjectionInflationRate}
                        onProjectionInflationRateReset={() => setProjectionInflationRate(null)}
                        onProjectionReset={handleResetProjectionSettings}
                        onProjectionResetFiSettings={handleResetFiSettings}
                        onProjectionResetWithdrawalSimulation={handleResetWithdrawalSimulation}
                    />
                )}

            </div>
        </Layout>
    );
}
